import {
	Box,
	Dialog,
	DialogContent,
	DialogTitle,
	Grid,
	IconButton,
	InputAdornment,
	InputLabel,
	TextField,
	Typography
} from "@mui/material";
import React, { FC, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { LoadingButton } from "@mui/lab";
import { useChangeUserPasswordMutation } from "../../../../redux/reducers/configuration.reducer";
import { useAppDispatch } from "../../../../redux";
import { showFeedbackNotification } from "../../../../redux/reducers/notifications.reducer";
import { PASSWORD_REGEX } from "../../../../utils/constants";

interface IChangePasswordDialogProps {
	open: boolean;
	userId: number;
	onClose: () => void;
}

enum SHOW_PASSWORD_KEY_E {
	OLD,
	NEW,
	CONFIRM
}

type ShowPasswordType = {
	[key in SHOW_PASSWORD_KEY_E]: boolean;
};

interface IChangePasswordFormFields {
	old_password: string;
	new_password: string;
	confirm_new_password: string;
}

const ChangePasswordDialog: FC<IChangePasswordDialogProps> = (props) => {
	const { open, userId, onClose } = props;

	const dispatch = useAppDispatch();

	const [showPassword, setShowPassword] = useState<ShowPasswordType>({
		[SHOW_PASSWORD_KEY_E.OLD]: false,
		[SHOW_PASSWORD_KEY_E.NEW]: false,
		[SHOW_PASSWORD_KEY_E.CONFIRM]: false
	});

	const [
		changeUserPassword,
		{
			isLoading: changeUserPasswordLoading,
			isSuccess: changeUserPasswordSuccess,
			error: changeUserPasswordError,
			reset: resetChangeUserPasswordState
		}
	] = useChangeUserPasswordMutation();

	const {
		reset,
		watch,
		register,
		handleSubmit,
		formState: { errors }
	} = useForm<IChangePasswordFormFields>();

	const newPasswordInput = watch("new_password");

	function handleToggleShowPassword(key: SHOW_PASSWORD_KEY_E) {
		setShowPassword((currentShowPasswordState) => ({
			...currentShowPasswordState,
			[key]: !currentShowPasswordState[key]
		}));
	}

	function handleSubmitForm(data: IChangePasswordFormFields) {
		if (userId >= 0) {
			changeUserPassword({
				user_id: userId,
				old_password: data.old_password,
				new_password: data.new_password
			});
		}
	}

	useEffect(() => {
		if (open) {
			reset({
				old_password: "",
				new_password: "",
				confirm_new_password: ""
			});
		} else {
			resetChangeUserPasswordState();
			setShowPassword({
				[SHOW_PASSWORD_KEY_E.OLD]: false,
				[SHOW_PASSWORD_KEY_E.NEW]: false,
				[SHOW_PASSWORD_KEY_E.CONFIRM]: false
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open, reset]);

	useEffect(() => {
		if (changeUserPasswordSuccess) {
			onClose();

			dispatch(
				showFeedbackNotification({
					message: "Password Updated Successfully",
					severity: "success"
				})
			);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [changeUserPasswordSuccess]);

	return (
		<Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
			<DialogTitle
				textAlign="center"
				sx={{
					background: "linear-gradient(135deg, #5b4ebf 0%, #403685 100%)",
					color: "white",
					position: "relative",
					padding: "16px 20px 12px 20px",
					borderBottom: "1px solid rgba(255, 255, 255, 0.1)"
				}}
			>
				<Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
					<Typography variant="h6" sx={{ fontWeight: 600 }}>
						Change Password
					</Typography>
				</Box>
				<Box sx={{ position: "absolute", top: 12, right: 12 }}>
					<IconButton
						onClick={onClose}
						sx={{
							color: "white",
							background: "rgba(255, 255, 255, 0.1)",
							backdropFilter: "blur(8px)",
							borderRadius: "50%",
							padding: "6px",
							"&:hover": {
								background: "rgba(255, 255, 255, 0.2)",
								transform: "scale(1.1)"
							},
							transition: "all 0.2s ease-in-out"
						}}
					>
						<CloseIcon />
					</IconButton>
				</Box>
			</DialogTitle>

			<DialogContent classes={{ root: "dialog-content-root" }}>
				<Box component="form" noValidate onSubmit={handleSubmit(handleSubmitForm)}>
					<Grid container spacing={2}>
						<Grid item xs={12}>
							<InputLabel
								required
								htmlFor="old-password-input"
								error={!!errors.old_password}
								sx={{
									fontWeight: 600,
									color: "#3D3D3D",
									marginBottom: "0.5rem",
									fontSize: "0.875rem"
								}}
							>
								Old Password
							</InputLabel>

							<TextField
								autoFocus
								required
								fullWidth
								type={showPassword[SHOW_PASSWORD_KEY_E.OLD] ? "text" : "password"}
								autoComplete="new-password"
								variant="outlined"
								color="primary"
								size="small"
								id="old-password-input"
								placeholder="Enter old password"
								error={!!errors.old_password}
								helperText={
									errors.old_password && errors.old_password.message ? errors.old_password.message.toString() : ""
								}
								InputProps={{
									endAdornment: (
										<InputAdornment position="end">
											<IconButton
												size="small"
												onClick={() => handleToggleShowPassword(SHOW_PASSWORD_KEY_E.OLD)}
												tabIndex={-1}
											>
												{showPassword[SHOW_PASSWORD_KEY_E.OLD] ? <VisibilityOffIcon /> : <VisibilityIcon />}
											</IconButton>
										</InputAdornment>
									)
								}}
								{...register("old_password", { required: "Old password is required" })}
							/>
						</Grid>

						<Grid item xs={12}>
							<InputLabel
								required
								htmlFor="new-password-input"
								error={!!errors.new_password}
								sx={{
									fontWeight: 600,
									color: "#3D3D3D",
									marginBottom: "0.5rem",
									fontSize: "0.875rem"
								}}
							>
								New Password
							</InputLabel>

							<TextField
								fullWidth
								type={showPassword[SHOW_PASSWORD_KEY_E.NEW] ? "text" : "password"}
								autoComplete="new-password"
								variant="outlined"
								color="primary"
								size="small"
								id="new-password-input"
								placeholder="Enter new password"
								error={!!errors.new_password}
								helperText={
									errors.new_password && errors.new_password.message ? errors.new_password.message.toString() : ""
								}
								InputProps={{
									endAdornment: (
										<InputAdornment position="end">
											<IconButton
												size="small"
												onClick={() => handleToggleShowPassword(SHOW_PASSWORD_KEY_E.NEW)}
												tabIndex={-1}
											>
												{showPassword[SHOW_PASSWORD_KEY_E.NEW] ? <VisibilityOffIcon /> : <VisibilityIcon />}
											</IconButton>
										</InputAdornment>
									)
								}}
								{...register("new_password", {
									required: "New password is required",
									pattern: {
										value: PASSWORD_REGEX,
										message: "Password should contain at least one alphabet, one number and a special character"
									},
									minLength: {
										value: 8,
										message: "Password should be at least 8 characters long"
									}
								})}
							/>
						</Grid>

						<Grid item xs={12}>
							<InputLabel
								required
								htmlFor="confirm-password-input"
								error={!!errors.confirm_new_password}
								sx={{
									fontWeight: 600,
									color: "#3D3D3D",
									marginBottom: "0.5rem",
									fontSize: "0.875rem"
								}}
							>
								Confirm New Password
							</InputLabel>

							<TextField
								fullWidth
								type={showPassword[SHOW_PASSWORD_KEY_E.CONFIRM] ? "text" : "password"}
								autoComplete="new-password"
								variant="outlined"
								color="primary"
								size="small"
								id="confirm-password-input"
								placeholder="Confirm New Password"
								error={!!errors.confirm_new_password}
								helperText={
									errors.confirm_new_password && errors.confirm_new_password.message
										? errors.confirm_new_password.message.toString()
										: ""
								}
								InputProps={{
									endAdornment: (
										<InputAdornment position="end">
											<IconButton
												size="small"
												onClick={() => handleToggleShowPassword(SHOW_PASSWORD_KEY_E.CONFIRM)}
												tabIndex={-1}
											>
												{showPassword[SHOW_PASSWORD_KEY_E.CONFIRM] ? <VisibilityOffIcon /> : <VisibilityIcon />}
											</IconButton>
										</InputAdornment>
									)
								}}
								{...register("confirm_new_password", {
									required: "Confirm password is required",
									validate: (value) =>
										value !== newPasswordInput ? "Confirm password should be same as password" : true
								})}
							/>
						</Grid>

						{changeUserPasswordError ? (
							<Grid item xs={12}>
								<Typography variant="body2" color="var(--color-error-main)" textAlign="center">
									{"data" in changeUserPasswordError
										? `${String(changeUserPasswordError.data).slice(0, 1).toUpperCase()}${String(
												changeUserPasswordError.data
										  ).slice(1)}`
										: "Something went wrong"}
								</Typography>
							</Grid>
						) : null}

						<Grid item xs={12}>
							<Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
								<LoadingButton
									disableElevation
									variant="contained"
									color="primary"
									type="submit"
									loading={changeUserPasswordLoading}
								>
									Save
								</LoadingButton>
							</Box>
						</Grid>
					</Grid>
				</Box>
			</DialogContent>
		</Dialog>
	);
};

export default ChangePasswordDialog;
